import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, User, Phone, Mail, Edit, MessageCircle, Loader2 } from 'lucide-react';
import api from '../lib/api';

const roleLabels = {
    admin: { label: 'Admin', color: 'bg-red-500' },
    super_admin: { label: 'Super Admin', color: 'bg-purple-500' },
    office_agent: { label: 'Agent Bureau', color: 'bg-blue-500' },
    confirmateur: { label: 'Confirmateur', color: 'bg-orange-500' },
    vendeur: { label: 'Vendeur', color: 'bg-green-500' },
    driver: { label: 'Livreur', color: 'bg-yellow-600' },
};

export default function UsersPage() {
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchUsers = async () => {
        try {
            const response = await api.get('/users');
            setUsers(response.data || []);
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    // Group users by role
    const groupedUsers = users.reduce((acc, user) => {
        const role = user.role || 'other';
        if (!acc[role]) acc[role] = [];
        acc[role].push(user);
        return acc;
    }, {});

    const roleOrder = ['admin', 'super_admin', 'confirmateur', 'office_agent', 'vendeur', 'driver'];

    if (loading) {
        return (
            <Layout>
                <div className="flex justify-center items-center min-h-[50vh]">
                    <Loader2 className="w-8 h-8 animate-spin" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="p-4 max-w-4xl mx-auto">
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold">Utilisateurs</h1>
                    <Button onClick={() => navigate('/utilisateurs/nouveau')}>
                        <Plus className="w-4 h-4 mr-2" />
                        Nouveau
                    </Button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                    <Card className="p-3 text-center">
                        <div className="text-2xl font-bold">{users.length}</div>
                        <div className="text-xs text-muted-foreground">Total</div>
                    </Card>
                    <Card className="p-3 text-center">
                        <div className="text-2xl font-bold text-green-600">
                            {users.filter(u => u.is_active !== false).length}
                        </div>
                        <div className="text-xs text-muted-foreground">Actifs</div>
                    </Card>
                    <Card className="p-3 text-center">
                        <div className="text-2xl font-bold text-blue-600">
                            {users.filter(u => u.telegram_chat_id).length}
                        </div>
                        <div className="text-xs text-muted-foreground">Telegram</div>
                    </Card>
                </div>

                {/* Users by Role */}
                <div className="space-y-6">
                    {roleOrder.map(role => {
                        const roleUsers = groupedUsers[role] || [];
                        if (roleUsers.length === 0) return null;

                        const roleInfo = roleLabels[role] || { label: role, color: 'bg-gray-500' };

                        return (
                            <div key={role}>
                                <div className="flex items-center gap-2 mb-3">
                                    <Badge className={`${roleInfo.color} text-white`}>
                                        {roleInfo.label}
                                    </Badge>
                                    <span className="text-sm text-muted-foreground">
                                        ({roleUsers.length})
                                    </span>
                                </div>

                                <div className="grid gap-3">
                                    {roleUsers.map(user => (
                                        <Card 
                                            key={user.id}
                                            className={`cursor-pointer hover:shadow-md transition-shadow ${
                                                user.is_active === false ? 'opacity-50' : ''
                                            }`}
                                            onClick={() => navigate(`/utilisateurs/${user.id}`)}
                                        >
                                            <CardContent className="p-4">
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1 min-w-0">
                                                        {/* Name */}
                                                        <div className="flex items-center gap-2">
                                                            <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                                            <span className="font-semibold truncate">
                                                                {user.name}
                                                            </span>
                                                            {user.is_active === false && (
                                                                <Badge variant="outline" className="text-red-500 border-red-500 text-xs">
                                                                    Inactif
                                                                </Badge>
                                                            )}
                                                        </div>

                                                        {/* Email */}
                                                        <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                                                            <Mail className="w-3 h-3 flex-shrink-0" />
                                                            <span className="truncate">{user.email}</span>
                                                        </div>

                                                        {/* Phone & Telegram */}
                                                        <div className="flex flex-wrap gap-3 mt-2">
                                                            {user.phone && (
                                                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                                    <Phone className="w-3 h-3" />
                                                                    <span>{user.phone}</span>
                                                                </div>
                                                            )}
                                                            {user.telegram_chat_id && (
                                                                <div className="flex items-center gap-1 text-xs text-blue-600">
                                                                    <MessageCircle className="w-3 h-3" />
                                                                    <span>Telegram ✓</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Edit Button */}
                                                    <Button 
                                                        variant="ghost" 
                                                        size="icon"
                                                        className="flex-shrink-0"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigate(`/utilisateurs/${user.id}`);
                                                        }}
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </Layout>
    );
}
